import React, { useState, useEffect } from "react";
import "../styles/StudentList.css";
import {
  getStudentAllotment,
  updateStudentStatus,
} from "../api/StudentDetails";
import { StudentSeatAllotment } from "../interfaces/StudentInterface";
import { pagination } from "../interfaces/baseinterface";
import { getDashboardDetails } from "../api/DashBoardDetails";
import { DashboardRequest } from "../interfaces/DashboardInterface";

const StudentList: React.FC = () => {
  // Helper function to format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const [students, setStudents] = useState<StudentSeatAllotment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"all" | "allotted" | "unallotted">(
    "all"
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedStudent, setSelectedStudent] =
    useState<StudentSeatAllotment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const imageUrl = process.env.REACT_APP_API_GET_IMAGE_URL || "";
  const [pagination, setPagination] = useState<pagination>({
    pageNumber: 1,
    pageSize: 5,
  });

  const [request, setRequest] = useState<DashboardRequest>({
    isTotalStudent: true,
    isOccupiedStudent: true,
    isAvailableChair: false,
    isThisMonthEnquiries: false,
    isThisMonthRevenue: false,
    isTotalRevenue: false,
  });

  // Fetch students with server-side pagination
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await getStudentAllotment(pagination);
      const dashboardData = await getDashboardDetails(request);
      if (response?.data) {
        setStudents(response.data || []);
        // If API returns total count, use it. Otherwise, estimate based on data length
        // You may need to adjust this based on your API response structure
        if (dashboardData.data?.totalStudentCount !== undefined) {
          setTotalCount(dashboardData.data?.totalStudentCount);
        } else {
          // If we got a full page, there might be more data
          setTotalCount(
            response.data.length === pagination.pageSize
              ? pagination.pageNumber * pagination.pageSize + 1
              : (pagination.pageNumber - 1) * pagination.pageSize +
                  response.data.length
          );
        }
      }
    } catch (error) {
      console.error("Error fetching student allotment:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch students when pagination changes
  useEffect(() => {
    fetchStudents();
  }, [pagination.pageNumber, pagination.pageSize]);

  // Reset to page 1 when tab or search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [activeTab, searchTerm]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  // Filter students client-side (only on current page data)
  // Note: For proper filtering, this should be done server-side
  const getFilteredStudents = (): StudentSeatAllotment[] => {
    let filtered: StudentSeatAllotment[] = [...students];

    // Filter by allotment status
    if (activeTab === "allotted") {
      filtered = filtered.filter(
        (student) => student.allotmentStatus === "Allotted"
      );
    } else if (activeTab === "unallotted") {
      filtered = filtered.filter(
        (student) => student.allotmentStatus === "Unallotted"
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (student) =>
          student.firstName?.toLowerCase().includes(term) ||
          student.lastName?.toLowerCase().includes(term) ||
          student.emailId?.toLowerCase().includes(term) ||
          student.mobileNumber?.includes(term) ||
          student.rfIdentificationCode?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const filteredStudents = getFilteredStudents();

  // Pagination calculations based on total count from server
  const totalPages = Math.ceil(totalCount / pagination.pageSize);
  const startIndex = (pagination.pageNumber - 1) * pagination.pageSize;
  const endIndex = startIndex + filteredStudents.length;

  // Generate page numbers to display
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      if (pagination.pageNumber > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, pagination.pageNumber - 1);
      const end = Math.min(totalPages - 1, pagination.pageNumber + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (pagination.pageNumber < totalPages - 2) {
        pages.push("...");
      }

      // Show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setPagination((prev) => ({ ...prev, pageNumber: page }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleStudentClick = (student: StudentSeatAllotment) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
  };

  const handleToggleStatus = async () => {
    if (!selectedStudent || !selectedStudent.studentDetailsId) return;

    try {
      setUpdatingStatus(true);
      const newStatus = !selectedStudent.isActive;
      await updateStudentStatus(selectedStudent.studentDetailsId, newStatus);

      // Update the student in the list
      setStudents((prev) =>
        prev.map((s) =>
          s.emailId === selectedStudent.emailId
            ? { ...s, isActive: newStatus }
            : s
        )
      );

      // Update selected student
      setSelectedStudent({ ...selectedStudent, isActive: newStatus });
    } catch (error) {
      console.error("Error updating student status:", error);
      alert("Failed to update student status. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="student-list-container">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading students...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-list-container">
      <div className="student-list-header">
        <h1>Student List</h1>
        <div className="header-actions">
          <div className="search-box">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="m21 21-4.35-4.35"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => {
              fetchStudents();
            }}
            className="refresh-btn"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 4v6h6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23 20v-6h-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      <div className="student-list-tabs">
        <button
          className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          All Students ({totalCount})
        </button>
        <button
          className={`tab-btn ${activeTab === "allotted" ? "active" : ""}`}
          onClick={() => setActiveTab("allotted")}
        >
          Allotted
        </button>
        <button
          className={`tab-btn ${activeTab === "unallotted" ? "active" : ""}`}
          onClick={() => setActiveTab("unallotted")}
        >
          Unallotted
        </button>
      </div>

      <div className="student-list-content">
        {/* Items per page selector */}
        {filteredStudents.length > 0 && (
          <div className="pagination-controls-top">
            <div className="items-per-page">
              <label htmlFor="itemsPerPage">Items per page:</label>
              <select
                id="itemsPerPage"
                value={pagination.pageSize}
                onChange={(e) => {
                  setPagination({
                    pageNumber: 1,
                    pageSize: Number(e.target.value),
                  });
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="pagination-info">
              Showing {startIndex + 1} to {endIndex} of {totalCount} students
            </div>
          </div>
        )}
        {filteredStudents.length === 0 ? (
          <div className="empty-state">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="9"
                cy="7"
                r="4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3>No students found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : activeTab === "allotted"
                ? "No students have been allotted seats yet"
                : activeTab === "unallotted"
                ? "All students have been allotted seats"
                : "No students registered yet"}
            </p>
          </div>
        ) : (
          <div className="student-table-container">
            <table className="student-table">
              <thead>
                <tr>
                  <th className="mobile-visible">Name</th>
                  <th className="mobile-hidden">Email</th>
                  <th className="mobile-hidden">Mobile</th>
                  <th className="mobile-hidden">RFID Code</th>
                  <th className="mobile-hidden">Seat Number</th>
                  <th className="mobile-hidden">Joining Date</th>
                  <th className="mobile-hidden">Allotment Status</th>
                  <th className="mobile-hidden">Active Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, index) => {
                  const isAllotted = student.allotmentStatus === "Allotted";
                  return (
                    <tr
                      key={`${student.studentDetailsId}`}
                      className="student-row clickable"
                      onClick={() => handleStudentClick(student)}
                    >
                      <td className="mobile-visible">
                        <div className="student-name-cell">
                          <div className="student-avatar">
                            {student.firstName?.charAt(0).toUpperCase() || "S"}
                          </div>
                          <div>
                            <div
                              className="student-name clickable"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStudentClick(student);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {student.firstName} {student.lastName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="mobile-hidden">
                        {student.emailId || "-"}
                      </td>
                      <td className="mobile-hidden">
                        {student.mobileNumber || "-"}
                      </td>
                      <td className="mobile-hidden">
                        {student.rfIdentificationCode ? (
                          <span className="rfid-badge">
                            {student.rfIdentificationCode}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="mobile-hidden">
                        {student.seatNumber ? (
                          <span className="seat-badge">
                            {student.seatNumber}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="mobile-hidden">
                        {formatDate(student.dateOfJoining || undefined)}
                      </td>
                      <td className="mobile-hidden">
                        <span
                          className={`status-badge ${
                            isAllotted ? "allotted" : "unallotted"
                          }`}
                        >
                          {student.allotmentStatus}
                        </span>
                      </td>
                      <td className="mobile-hidden">
                        <span
                          className={`status-badge ${
                            student.isActive ? "active" : "inactive"
                          }`}
                        >
                          {student.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredStudents.length > 0 && totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.pageNumber - 1)}
                disabled={pagination.pageNumber === 1}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Previous
              </button>

              <div className="pagination-pages mobile-hidden">
                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === "..." ? (
                      <span className="pagination-ellipsis">...</span>
                    ) : (
                      <button
                        className={`pagination-page-btn ${
                          pagination.pageNumber === page ? "active" : ""
                        }`}
                        onClick={() => handlePageChange(page as number)}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.pageNumber + 1)}
                disabled={pagination.pageNumber === totalPages}
              >
                Next
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18l6-6-6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Student Details Modal */}
      {isModalOpen && selectedStudent && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div
            className="student-details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Student Details</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="modal-content">
              {/* Profile Picture Section */}
              <div className="student-profile-section">
                <div className="student-profile-picture">
                  {selectedStudent.imagePath ? (
                    <img
                      src={imageUrl + selectedStudent.imagePath}
                      alt={`${selectedStudent.firstName} ${selectedStudent.lastName}`}
                    />
                  ) : (
                    <div className="profile-placeholder">
                      {selectedStudent.firstName?.charAt(0).toUpperCase() ||
                        "S"}
                      {selectedStudent.lastName?.charAt(0).toUpperCase() || ""}
                    </div>
                  )}
                </div>
                <h3>
                  {selectedStudent.firstName} {selectedStudent.lastName}
                </h3>
                <div className="profile-status-badges">
                  <span
                    className={`status-badge ${
                      selectedStudent.isActive ? "active" : "inactive"
                    }`}
                  >
                    {selectedStudent.isActive ? "Active" : "Inactive"}
                  </span>
                  <span
                    className={`status-badge ${
                      selectedStudent.allotmentStatus === "Allotted"
                        ? "allotted"
                        : "unallotted"
                    }`}
                  >
                    {selectedStudent.allotmentStatus}
                  </span>
                </div>
              </div>

              {/* Student Details Grid */}
              <div className="student-details-grid">
                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="22,6 12,13 2,6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Email
                  </label>
                  <div className="detail-value">
                    {selectedStudent.emailId || "-"}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <path
                        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Mobile Number
                  </label>
                  <div className="detail-value">
                    <a href={`tel:${selectedStudent.mobileNumber}`}>
                      {selectedStudent.mobileNumber || "-"}
                    </a>
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <rect
                        x="3"
                        y="11"
                        width="18"
                        height="11"
                        rx="2"
                        ry="2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7 11V7a5 5 0 0 1 10 0v4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    RFID Code
                  </label>
                  <div className="detail-value">
                    {selectedStudent.rfIdentificationCode ? (
                      <span className="rfid-badge">
                        {selectedStudent.rfIdentificationCode}
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <path
                        d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="9 22 9 12 15 12 15 22"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Seat Number
                  </label>
                  <div className="detail-value">
                    {selectedStudent.seatNumber ? (
                      <span className="seat-badge">
                        {selectedStudent.seatNumber}
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="12 6 12 12 16 14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Joining Date
                  </label>
                  <div className="detail-value">
                    {formatDate(selectedStudent.dateOfJoining || undefined)}
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <path
                        d="M9 11l3 3L22 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Allotment Status
                  </label>
                  <div className="detail-value">
                    <span
                      className={`status-badge ${
                        selectedStudent.allotmentStatus === "Allotted"
                          ? "allotted"
                          : "unallotted"
                      }`}
                    >
                      {selectedStudent.allotmentStatus}
                    </span>
                  </div>
                </div>

                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <path
                        d="M9 11l3 3L22 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Address
                  </label>
                  <div className="detail-value">
                    <div className="detail-value">
                      {selectedStudent.address || "-"}
                    </div>
                  </div>
                </div>
                <div className="detail-item">
                  <label>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="detail-icon"
                    >
                      <path
                        d="M9 11l3 3L22 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Aadhar Number
                  </label>
                  <div className="detail-value">
                    <div className="detail-value">
                      {selectedStudent.aadharNumber || "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="modal-actions">
                <button
                  className={`status-toggle-btn ${
                    selectedStudent.isActive ? "deactivate" : "activate"
                  }`}
                  onClick={handleToggleStatus}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? (
                    <>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="spinning"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeDasharray="32"
                          strokeDashoffset="32"
                        >
                          <animate
                            attributeName="stroke-dasharray"
                            dur="2s"
                            values="0 32;16 16;0 32;0 32"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="stroke-dashoffset"
                            dur="2s"
                            values="0;-16;-32;-32"
                            repeatCount="indefinite"
                          />
                        </circle>
                      </svg>
                      Updating...
                    </>
                  ) : selectedStudent.isActive ? (
                    <>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Deactivate Student
                    </>
                  ) : (
                    <>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 6L9 17l-5-5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Activate Student
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;
